import { useState, useEffect, useCallback } from "react";

import { Tooltip } from "flowbite-react";

import { FEATURE_FLAGS, useFeatureFlag } from "../../contexts";
import { revenueMetricsService } from "../../services/RevenueMetricsService";
import { getFormattedMoney } from "../../utils";
import { buildDashboardSparkline } from "./constants";
import { BarChartIcon, ChevronDownIcon } from "./icons";

const getActiveRevenueTooltip = (revenueUnit) =>
  `Subscription ${revenueUnit} + paid pilot ${revenueUnit}. Pilots are billed but not yet under committed renewal contract.`;

// Metric definitions: key in API response → display config
const METRIC_DEFS = [
  {
    key: "forecast_mrr",
    label: "Forecast MRR",
    isMoney: true,
    gradId: "v2SparkGrad0",
  },
  {
    key: "net_new_mrr",
    label: "Net New MRR",
    isMoney: true,
    // Net New is itself a change figure, so a % of it is a change of a change and swings
    // wildly off a small base (€20 → €250 is +1,150%). Its chip is always the money change
    // vs the comparison point, as on the HubSpot App Home.
    absoluteChange: true,
    gradId: "v2SparkGrad1",
  },
  {
    key: "net_revenue_retention_pct",
    label: "Net Revenue Retention",
    isMoney: false,
    gradId: "v2SparkGrad2",
  },
];

// Static class names — Tailwind can't see interpolated ones.
const GRID_COLS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const CHANGE_COLORS = {
  up: { text: "text-emerald-500", hex: "#34d399" },
  down: { text: "text-red-500", hex: "#f87171" },
  flat: { text: "text-slate-400", hex: "#94a3b8" },
};

const colorForChange = (signValue) =>
  signValue > 0
    ? CHANGE_COLORS.up
    : signValue < 0
      ? CHANGE_COLORS.down
      : CHANGE_COLORS.flat;

const formatChangePct = (changePct) => {
  if (changePct === null || changePct === undefined || changePct === 0)
    return "0.0% →";
  const sign = changePct > 0 ? "+" : "";
  const arrow = changePct > 0 ? "↑" : "↓";
  return `${sign}${changePct.toFixed(1)}% ${arrow}`;
};

const annualizeMoney = (money) => ({
  ...money,
  amount: parseFloat(money?.amount ?? 0) * 12,
  formatted: undefined,
});

const formatValue = (metric, def, revenueUnit) => {
  if (def.isMoney) {
    return getFormattedMoney(
      revenueUnit === "ARR" ? annualizeMoney(metric) : metric,
    );
  }
  const value = metric.value != null ? metric.value : 0;
  return `${value.toFixed(1)}%`;
};

const getMetricTooltip = (tooltip, revenueUnit) =>
  tooltip?.replaceAll("MRR", revenueUnit) ?? null;

// PAN-819: the backend answers for the calendar quarter and says so in `time_window`
// (PAN-882: the only window there is). The page follows the response, never its own
// clock, so the labels can't disagree with the numbers. Null until a response arrives.
export const getQuarterWindow = (data) =>
  data?.time_window?.type === "quarter" ? data.time_window : null;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "2026-12-31" → "Dec 31", read as a calendar date (no Date/timezone conversion).
export const formatMonthDay = (isoDate) => {
  const [, month, day] = (isoDate || "").split("-").map(Number);
  if (!month || !day) return "";
  return `${MONTHS[month - 1]} ${day}`;
};

const QUARTER_PERIOD_LABELS = {
  forecast_mrr: (quarter) => `by ${formatMonthDay(quarter.end)}`,
  net_new_mrr: () => "QTD",
  net_revenue_retention_pct: (quarter) =>
    `${quarter.label.split(" ")[0]} so far`,
  active_mrr: () => "as of today",
};

const QUARTER_TOOLTIPS = {
  forecast_mrr: (quarter) =>
    `Projected MRR on ${formatMonthDay(quarter.end)}, the last day of the quarter, paid pilots included.`,
  net_new_mrr: () =>
    "New, expansion, contraction and churn MRR since the quarter started.",
  net_revenue_retention_pct: () =>
    "MRR today from the customers who were paying on the first day of the quarter, as a share of what they paid then. Not annualised.",
};

export const comparisonText = (comparison) => {
  if (!comparison) return null;
  // PAN-876: the forecast's chip is the change expected by the quarter end, from Active MRR today.
  if (comparison.type === "today") return "vs today";
  if (comparison.type === "same_day_last_quarter")
    return `vs the same day in ${comparison.label}`;
  if (comparison.type === "end_of_last_quarter")
    return `vs the end of ${comparison.label}`;
  if (comparison.type === "quarter_start") return "vs the start of the quarter";
  if (comparison.type === "subscription_change_since_quarter_start")
    return "Subscription change since the start of the quarter";
  return null;
};

const buildEmptyNrrCaption = (m, revenueUnit) => {
  if (!m.available_from) return null;
  const scale = revenueUnit === "ARR" ? 12 : 1;
  const money = (key) =>
    getFormattedMoney({
      amount: parseFloat(m.movements?.[key]?.amount ?? 0) * scale,
      currency: m.movements?.[key]?.currency,
    });
  return `No customers were paying on day 1. Expansion ${money("expansion")}, contraction ${money("contraction")}, churn ${money("churn")}. NRR from ${formatMonthDay(m.available_from)}.`;
};

const buildMetricsFromResponse = (data, revenueUnit) => {
  if (!data) return [];
  const quarter = getQuarterWindow(data);

  return METRIC_DEFS.filter((def) => data[def.key]).map((def) => {
    const m = data[def.key];
    // PAN-819: a quarter tile the backend could not build — no number, never a made-up zero.
    // An NRR with no day-1 cohort is "—" too, with the quarter's movements underneath.
    const noValue = m.unavailable === true || (!def.isMoney && m.value == null);
    const sparkline = buildDashboardSparkline(m.historical_data);

    // Roadmap #6: for new/small accounts the backend suppresses the % chip
    // (change_pct=null when the previous-period baseline is at/below the
    // configured threshold) because the % would be "crazy/not useful". In that
    // case show the absolute money move ("+€200" / "−€100") instead of a
    // misleading "0.0%". Only money metrics carry change_absolute. An absoluteChange
    // tile (Net New) shows the money change whenever there is one, zero included.
    const pctSuppressed = m.change_pct === null || m.change_pct === undefined;
    const hasAbsolute = m.change_absolute != null;
    const useAbsolute = def.absoluteChange
      ? hasAbsolute
      : def.isMoney && pctSuppressed && hasAbsolute && m.change_absolute !== 0;

    const signValue = useAbsolute ? m.change_absolute : (m.change_pct ?? 0);
    const color = colorForChange(signValue);
    const absoluteSign =
      m.change_absolute > 0 ? "+" : m.change_absolute < 0 ? "−" : "";

    const changeText = useAbsolute
      ? `${absoluteSign}${getFormattedMoney({
          amount:
            Math.abs(m.change_absolute) *
            (def.isMoney && revenueUnit === "ARR" ? 12 : 1),
          currency: m.currency,
        })}`
      : formatChangePct(m.change_pct);
    // A missing % means there is nothing to compare with (e.g. no customers on day 1 of
    // last quarter): no chip, never a made-up "0.0%". An absoluteChange tile with no money
    // change (e.g. history that starts after last quarter began) has no chip either — it
    // never falls back to a %.
    const noComparison = !useAbsolute && (pctSuppressed || def.absoluteChange);

    return {
      id: def.gradId,
      label:
        def.isMoney && revenueUnit === "ARR"
          ? def.label.replace("MRR", "ARR")
          : def.label,
      tooltip: quarter
        ? getMetricTooltip(QUARTER_TOOLTIPS[def.key]?.(quarter), revenueUnit)
        : null,
      periodLabel: quarter
        ? (QUARTER_PERIOD_LABELS[def.key]?.(quarter) ?? null)
        : null,
      value: noValue ? "—" : formatValue(m, def, revenueUnit),
      change: noValue || noComparison ? "" : changeText,
      changeTitle: comparisonText(m.comparison),
      caption:
        noValue && !m.unavailable ? buildEmptyNrrCaption(m, revenueUnit) : null,
      changeClass: color.text,
      strokeColor: color.hex,
      gradColor: color.hex,
      linePath: sparkline?.linePath || "",
      areaPath: sparkline?.areaPath || "",
      endPoint: sparkline?.endPoint || null,
      breakdown: null,
    };
  });
};

/**
 * Active MRR card — built from `active_mrr_breakdown` (BE pano #1075):
 *   { subscription_mrr, pilot_mrr, active_mrr }  // money objects {amount, currency, formatted}
 *
 * Value + the "Subscription X + Pilot Y" breakdown are BE-backed. The trend
 * chip (+%) and sparkline are read null-safe from `active_mrr.{change_pct,
 * historical_data}` — those aren't in the documented shape yet, so the card
 * shows value + breakdown only until the BE adds them.
 */
const buildActiveRevenueMetric = (data, revenueUnit) => {
  const breakdown = data?.active_mrr_breakdown;
  if (!breakdown?.active_mrr) return null;

  const m = breakdown.active_mrr;
  const quarter = getQuarterWindow(data);
  // PAN-819: the backend could not build the block — no numbers, never a made-up €0.
  const unavailable = m.unavailable === true;
  const money = (value) =>
    unavailable
      ? "—"
      : getFormattedMoney(
          revenueUnit === "ARR" ? annualizeMoney(value) : value,
        );
  const sparkline = buildDashboardSparkline(m.historical_data);
  const hasChange =
    !unavailable && m.change_pct !== null && m.change_pct !== undefined;
  const color = colorForChange(m.change_pct ?? 0);

  return {
    id: "v2SparkGradActive",
    label: `Active ${revenueUnit}`,
    tooltip: getActiveRevenueTooltip(revenueUnit),
    periodLabel: quarter ? QUARTER_PERIOD_LABELS.active_mrr(quarter) : null,
    value: money(m),
    change: hasChange ? formatChangePct(m.change_pct) : "",
    changeTitle: comparisonText(m.comparison),
    changeClass: color.text,
    strokeColor: color.hex,
    gradColor: color.hex,
    linePath: sparkline?.linePath || "",
    areaPath: sparkline?.areaPath || "",
    endPoint: sparkline?.endPoint || null,
    breakdown: {
      subscriptionLabel: `Subscription ${revenueUnit}`,
      subscription: money(breakdown.subscription_mrr),
      pilotLabel: `Pilot ${revenueUnit}`,
      pilot: money(breakdown.pilot_mrr),
    },
  };
};

const InfoDot = ({ content, label }) => (
  <Tooltip
    content={content}
    // eslint-disable-next-line react/style-prop-object
    style="light"
  >
    <span
      className="inline-flex cursor-help items-center text-slate-400 hover:text-slate-500"
      role="img"
      aria-label={label}
    >
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </span>
  </Tooltip>
);

const MetricCell = ({ metric }) => (
  <div className="bg-white px-6 py-[18px]">
    <div className="mb-1.5 flex items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-slate-400">
        {metric.label}
      </span>
      {metric.periodLabel && (
        <span className="text-[11px] font-medium text-slate-400">
          {metric.periodLabel}
        </span>
      )}
      {metric.tooltip && (
        <InfoDot
          content={metric.tooltip}
          label={`${metric.label} information`}
        />
      )}
    </div>
    <div className="mb-2.5 flex items-baseline gap-2">
      <span className="text-[26px] font-extrabold tracking-[-0.8px] text-slate-800">
        {metric.value}
      </span>
      <span
        className={`text-xs font-bold ${metric.changeClass}`}
        title={metric.changeTitle || undefined}
      >
        {metric.change}
      </span>
    </div>
    {metric.caption && (
      <div className="mb-2.5 text-[12.5px] text-slate-500">
        {metric.caption}
      </div>
    )}
    {metric.breakdown && (
      <div className="mb-2.5 flex flex-wrap items-center gap-1.5 text-[12.5px] text-slate-500">
        <span>
          {metric.breakdown.subscriptionLabel}{" "}
          <b className="font-bold text-slate-800">
            {metric.breakdown.subscription}
          </b>
        </span>
        <span className="text-slate-300">+</span>
        <span>
          {metric.breakdown.pilotLabel}{" "}
          <b className="font-bold text-slate-800">{metric.breakdown.pilot}</b>
        </span>
      </div>
    )}
    {metric.linePath && (
      <div className="relative">
        <svg
          width="100%"
          height="32"
          viewBox="0 0 200 32"
          preserveAspectRatio="none"
          className="block"
        >
          <defs>
            <linearGradient id={metric.id} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={metric.gradColor}
                stopOpacity="0.15"
              />
              <stop
                offset="100%"
                stopColor={metric.gradColor}
                stopOpacity="0"
              />
            </linearGradient>
          </defs>
          <path
            d={metric.linePath}
            fill="none"
            stroke={metric.strokeColor}
            strokeWidth="2"
          />
          <path d={metric.areaPath} fill={`url(#${metric.id})`} />
        </svg>
        {metric.endPoint && (
          <span
            className="pointer-events-none absolute h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: `${(metric.endPoint.x / 200) * 100}%`,
              top: `${(metric.endPoint.y / 32) * 100}%`,
              backgroundColor: metric.strokeColor,
            }}
          />
        )}
      </div>
    )}
  </div>
);

const LoadingSkeleton = () => (
  <div
    data-testid="portfolio-loading"
    className="grid grid-cols-4 gap-px border-t border-slate-100 bg-slate-100"
  >
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="bg-white px-6 py-[18px]">
        <div className="mb-1.5 h-3 w-24 animate-pulse rounded bg-slate-200" />
        <div className="mb-2.5 flex items-baseline gap-2">
          <div className="h-7 w-28 animate-pulse rounded bg-slate-200" />
          <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
        </div>
        <div className="h-8 w-full animate-pulse rounded bg-slate-100" />
      </div>
    ))}
  </div>
);

export const PortfolioHealth = ({
  expanded,
  onToggle,
  revenueUnit = "MRR",
}) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pilotEnabled = useFeatureFlag(FEATURE_FLAGS.PILOT_PIPELINE_SUPPORT);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await revenueMetricsService.getTeamRevenueMetrics();
      setMetrics(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    window.addEventListener("ownerScopeChanged", fetchMetrics);
    return () => {
      window.removeEventListener("ownerScopeChanged", fetchMetrics);
    };
  }, [fetchMetrics]);

  // Active MRR is a pilots-feature surface — the tile (and its Subscription/
  // Pilot breakdown row) only makes sense for teams enrolled in
  // `pilot_pipeline_support`. Non-enrolled teams see the original 3 cards.
  const activeMrrCard = pilotEnabled
    ? buildActiveRevenueMetric(metrics, revenueUnit)
    : null;
  const metricCards = [
    ...(activeMrrCard ? [activeMrrCard] : []),
    ...buildMetricsFromResponse(metrics, revenueUnit),
  ];
  const gridColsClass = GRID_COLS[metricCards.length] ?? "grid-cols-4";
  const quarter = getQuarterWindow(metrics);

  return (
    <div className="mb-5">
      <div className="overflow-hidden rounded-[14px] border border-[#e8ecf0] bg-white">
        <button
          onClick={onToggle}
          className="flex w-full cursor-pointer items-center justify-between border-none bg-transparent px-6 pb-3 pt-4"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-600">
              <BarChartIcon />
            </span>
            <span className="text-[13px] font-bold text-slate-800">
              Portfolio Health
            </span>
            <Tooltip
              content={
                quarter
                  ? `Overview of your portfolio's revenue health in ${quarter.label} so far`
                  : "Overview of your portfolio's revenue health this quarter so far"
              }
              // eslint-disable-next-line react/style-prop-object
              style="light"
            >
              <span
                className="ml-1 inline-flex cursor-help items-center text-slate-400 hover:text-slate-500"
                role="img"
                aria-label="Portfolio health information"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </Tooltip>
            {quarter && (
              <span className="text-[11px] font-medium text-slate-400">
                {`— ${quarter.label} so far`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {!expanded && (
              <div className="flex gap-4">
                {loading ? (
                  <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
                ) : (
                  metricCards.map((metric) => (
                    <div
                      key={metric.id}
                      className="flex items-baseline gap-1 text-[11px]"
                    >
                      <span className="font-medium text-slate-400">
                        {metric.label}
                      </span>
                      <span className="text-sm font-bold text-black">
                        {metric.value}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
            <ChevronDownIcon expanded={expanded} />
          </div>
        </button>

        {expanded && (
          <>
            {loading && <LoadingSkeleton />}
            {error && !loading && (
              <div className="border-t border-slate-100 px-6 py-8 text-center text-sm text-red-500">
                Failed to load portfolio metrics. Please try again later.
              </div>
            )}
            {!loading && !error && metricCards.length > 0 && (
              <div
                className={`animation-slide-down-lil-bounce grid ${gridColsClass} gap-px border-t border-slate-100 bg-slate-100`}
              >
                {metricCards.map((metric) => (
                  <MetricCell key={metric.id} metric={metric} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
